// FLAGS: authorization, identity
// The gate on the scheduler shell: it turns "no profile" into a /login redirect and "no CustomerSuccess
// role" into a denial screen, and is the one listener for the api client's session-expiry event.
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation } from "react-router";
import { AUTH_EXPIRED_EVENT } from "@src/lib/apiClient";
import { authKeys, useCurrentUser } from "@src/queries/auth";
import isCustomerSuccess from "@src/auth/userRoles";
import AccessDenied from "@src/auth/AccessDenied";
import FullPageSpinner from "@src/auth/FullPageSpinner";
import ConnectionError from "@src/auth/ConnectionError";

/**
 * FLAGS: authorization, identity
 * Rendering the outlet before `isPending` resolves, or dropping the role test,
 * exposes the shell to any signed-in OpenUp account. It withholds UI, not data:
 * the API policy on each endpoint is what actually refuses a request.
 * Guards the admin shell: unauthenticated visitors go to the app's own
 * /login (AC3); authenticated users without the CustomerSuccess role are
 * denied with a clear message (AC2).
 */
const ProtectedRoute = () => {
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    data: user,
    isPending,
    isError,
    refetch,
    isFetching,
  } = useCurrentUser();

  // Single, centralized reaction to session expiry signalled by the
  // api client interceptor — clears the cached user; rendering below
  // then redirects to /login.
  useEffect(() => {
    const onAuthExpired = () => {
      queryClient.setQueryData(authKeys.me, null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, [queryClient]);

  if (isPending) {
    return <FullPageSpinner />;
  }

  // Ahead of the signed-out branch on purpose: `!user` is also true when the query errored, and
  // redirecting to /login on an outage tells a scheduler their session ended when it has not.
  if (isError) {
    return (
      <ConnectionError onRetry={() => refetch()} isRetrying={isFetching} />
    );
  }

  if (!user) {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to="/login" replace state={{ from }} />;
  }

  if (!isCustomerSuccess(user)) {
    return <AccessDenied user={user} />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
